import React, { useState, useEffect, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './components/USER/Auth/AuthContext';
import { useGlobalGPS } from './components/GPS/GPSContext';
import { motion, AnimatePresence } from 'framer-motion';

// Core routes (eagerly loaded for instant interaction)
import Home from './components/HOME/Home';
import LandingPage from './pages/LandingPage';
import RequestService from './components/DELIVERY/Service/RequestService';
import DriverRegister from './components/DELIVERY/Driver/DriverRegister';
import ServiceHistory from './components/DELIVERY/Service/ServiceHistory';
import Header from './components/UI/Header';
import SpeedometerTransition from './components/UI/PageTransition';
import ProtectedRoute from './components/UI/ProtectedRoute';
import OnboardingModal from './components/USER/OnboardingModal';
import GPSPermissionModal from './components/UI/GPSPermissionModal';
import ErrorBoundary from './components/UI/ErrorBoundary';
import { startScheduledServiceWorker, stopScheduledServiceWorker } from './services/ScheduledServiceWorker';
import OfflineBanner from './components/UI/OfflineBanner';
import "react-datepicker/dist/react-datepicker.css";

// Secondary & Admin Routes (Lazy loaded for massive bundle reduction)
const DayRegister = lazy(() => import('./components/DIARIA/DayRegister'));
const ConfigPanel = lazy(() => import('./components/ConfigPanel'));
const GraphPanel = lazy(() => import('./components/GraphPanel'));
const RecordList = lazy(() => import('./components/RecordList'));
const Collaborate = lazy(() => import('./components/Collaborate'));
const ConfigPage = lazy(() => import('./components/USER/CONFIG/ConfigPage'));
const RegistrarGanhos = lazy(() => import('./components/Earnings/RegisterEarnings'));
const ListEarnings = lazy(() => import('./components/Earnings/ListEarnings'));
const ListFuelings = lazy(() => import('./components/Fuelings/ListFuelings'));
const RegisterBike = lazy(() => import('./components/BIKE/RegisterBike'));
const ViewBike = lazy(() => import('./components/BIKE/ViewBike'));
const PriceCalculator = lazy(() => import('./components/DELIVERY/Calculator/PriceCalculator'));
const AdminPanel = lazy(() => import('./components/ADMIN/AdminPanel'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const UserManagement = lazy(() => import('./components/ADMIN/UserManagement'));
const RolesManager = lazy(() => import('./components/ADMIN/RolesManager'));
const PublicTracking = lazy(() => import('./pages/PublicTracking'));
const ConfirmService = lazy(() => import('./pages/ConfirmService'));
const ProfilePage = lazy(() => import('./components/USER/ProfilePage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethodsPage'));
const ShiftsPage = lazy(() => import('./pages/ShiftsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const SupportTicketsPage = lazy(() => import('./pages/SupportTicketsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const AdminDrivers = lazy(() => import('./components/ADMIN/AdminDrivers'));
const AdminServices = lazy(() => import('./components/ADMIN/AdminServices'));
const AdminTariffs = lazy(() => import('./components/ADMIN/AdminTariffs'));
const AdminDocuments = lazy(() => import('./components/ADMIN/AdminDocuments'));
const AdminBikes = lazy(() => import('./components/ADMIN/AdminBikes'));
const AdminEarnings = lazy(() => import('./components/ADMIN/AdminEarnings'));
const AdminPayouts = lazy(() => import('./components/ADMIN/AdminPayouts'));
const AdminShifts = lazy(() => import('./components/ADMIN/AdminShifts'));
const AdminServiceCatalog = lazy(() => import('./components/ADMIN/AdminServiceCatalog'));
const AdminDriverProfile = lazy(() => import('./components/ADMIN/AdminDriverProfile'));
const DriverPublicProfile = lazy(() => import('./components/DELIVERY/Driver/DriverPublicProfile'));
const AdminReviews = lazy(() => import('./components/ADMIN/AdminReviews'));
const AdminSpoofAlerts = lazy(() => import('./components/ADMIN/AdminSpoofAlerts'));
const RegisterPaymentPage = lazy(() => import('./pages/RegisterPaymentPage'));
const AdminPaymentConfig = lazy(() => import('./components/ADMIN/AdminPaymentConfig'));
const ScheduledRidesPage = lazy(() => import('./pages/ScheduledRidesPage').then(m => ({ default: m.ScheduledRidesPage })));
const AdminScheduledServices = lazy(() => import('./components/ADMIN/AdminScheduledServices').then(m => ({ default: m.AdminScheduledServices })));

function App() {
  const { currentUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      window.dispatchEvent(new CustomEvent('pwa-installable', { detail: true }));
    };

    const handleAppInstalled = () => {
      window.deferredPrompt = null;
      window.dispatchEvent(new CustomEvent('pwa-installable', { detail: false }));
      console.log('Bryemma PWA instalada con éxito!');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      startScheduledServiceWorker();
      return () => stopScheduledServiceWorker();
    }
  }, [currentUser]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <OfflineBanner />
      <AppShell loading={loading} setLoading={setLoading} currentUser={currentUser} authLoading={authLoading} />
    </Router>
  );
}

function AppShell({ loading, setLoading, currentUser, authLoading }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/soporte';
  const gps = useGlobalGPS();

  // Admin routes: render FUERA del .App container, layout completo desktop
  if (isAdminRoute) {
    return (
      <>
        <AppRoutes loading={loading} setLoading={setLoading} currentUser={currentUser} authLoading={authLoading} />
        {currentUser && <OnboardingModal />}
        <GPSPermissionModal
          show={gps.showPermissionModal}
          onRequestPermission={gps.requestGpsPermission}
          onDeny={gps.denyGpsPermission}
        />
      </>
    );
  }

  // Mobile app: dentro del .App container sin BottomNav (navegación en AppSidebar vía Header Logo)
  return (
    <div className="App">
      {location.pathname !== '/' && location.pathname !== '/admin/login' && (
        <Header userName={currentUser?.email || "Visitante"} />
      )}
      <AppRoutes loading={loading} setLoading={setLoading} currentUser={currentUser} authLoading={authLoading} />
      <SpeedometerTransition isVisible={loading} />
      {currentUser && <OnboardingModal />}
      <GPSPermissionModal
        show={gps.showPermissionModal}
        onRequestPermission={gps.requestGpsPermission}
        onDeny={gps.denyGpsPermission}
      />
    </div>
  );
}

function AppRoutes({ loading, setLoading, currentUser, authLoading }) {
  const location = useLocation();
  const { maxRoleLevel, isAdmin, isSuperAdmin } = useAuth();

  return (
    <ErrorBoundary>
      <Suspense fallback={<SpeedometerTransition isVisible={true} />}>
        <AnimatePresence>
          <Routes key={location.pathname} location={location}>
        <Route
          path="/"
          element={
            // Wait for auth to initialize before deciding to show login or redirect.
            authLoading ? null : (!currentUser ? (
              <LandingPage />
            ) : (
              (isSuperAdmin || isAdmin || maxRoleLevel >= 4) ? (
                <Navigate to="/admin" replace={true} />
              ) : (
                <Navigate to="/home" replace={true} />
              )
            ))
          }
        />
        <Route path="/track/:token" element={<PublicTracking />} />
        <Route path="/confirmar/:token" element={<ConfirmService />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terminos" element={<TermsPage />} />
        <Route path="/privacidad" element={<PrivacyPage />} />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <AnimatedPage><Home /></AnimatedPage>
            </ProtectedRoute>
          }
        />
        {/* ── Rutas solo de conductor (role=driver, level 2) ─────────────────── */}
        <Route path="/day-register" element={<ProtectedRoute role="driver"><AnimatedPage><DayRegister /></AnimatedPage></ProtectedRoute>} />
        <Route path="/registrar-ganhos" element={<ProtectedRoute role="driver"><AnimatedPage><RegistrarGanhos /></AnimatedPage></ProtectedRoute>} />
        <Route path="/listar-ganhos" element={<ProtectedRoute role="driver"><AnimatedPage><ListEarnings /></AnimatedPage></ProtectedRoute>} />
        <Route path="/listar-abastecimento" element={<ProtectedRoute role="driver"><AnimatedPage><ListFuelings /></AnimatedPage></ProtectedRoute>} />
        <Route path="/registrar" element={<ProtectedRoute><AnimatedPage><RegisterBike /></AnimatedPage></ProtectedRoute>} />
        <Route path="/view-bike" element={<ProtectedRoute><AnimatedPage><ViewBike /></AnimatedPage></ProtectedRoute>} />
        <Route path="/registro-conductor" element={<ProtectedRoute><AnimatedPage><DriverRegister /></AnimatedPage></ProtectedRoute>} />
        <Route path="/turnos" element={<ProtectedRoute role="driver"><AnimatedPage><ShiftsPage /></AnimatedPage></ProtectedRoute>} />
        <Route path="/documentos" element={<ProtectedRoute><AnimatedPage><DocumentsPage /></AnimatedPage></ProtectedRoute>} />
        {/* ── Rutas solo de pasajero (role=passenger) ──────────────────────── */}
        <Route path="/favoritos" element={<ProtectedRoute role="passenger"><AnimatedPage><FavoritesPage /></AnimatedPage></ProtectedRoute>} />
        {/* ── Rutas compartidas (todos los usuarios autenticados) ───────────── */}
        <Route path="/metodos-pago" element={<ProtectedRoute><AnimatedPage><PaymentMethodsPage /></AnimatedPage></ProtectedRoute>} />
        <Route path="/config-panel" element={<ProtectedRoute><AnimatedPage><ConfigPanel /></AnimatedPage></ProtectedRoute>} />
        <Route path="/graph-panel" element={<ProtectedRoute><AnimatedPage><GraphPanel /></AnimatedPage></ProtectedRoute>} />
        <Route path="/list" element={<ProtectedRoute><AnimatedPage><RecordList /></AnimatedPage></ProtectedRoute>} />
        <Route path="/collaborate" element={<ProtectedRoute><AnimatedPage><Collaborate /></AnimatedPage></ProtectedRoute>} />
        <Route path="/config" element={<ProtectedRoute><AnimatedPage><ConfigPage /></AnimatedPage></ProtectedRoute>} />
        <Route path="/calculadora" element={<ProtectedRoute><AnimatedPage><PriceCalculator /></AnimatedPage></ProtectedRoute>} />
        <Route path="/solicitar-servicio" element={<ProtectedRoute role="passenger"><AnimatedPage><RequestService /></AnimatedPage></ProtectedRoute>} />
        <Route path="/historial-servicios" element={<ProtectedRoute><AnimatedPage><ServiceHistory /></AnimatedPage></ProtectedRoute>} />
        <Route path="/mis-viajes-programados" element={<ProtectedRoute><AnimatedPage><ScheduledRidesPage /></AnimatedPage></ProtectedRoute>} />
        <Route path="/evaluaciones" element={<ProtectedRoute><AnimatedPage><ReviewsPage /></AnimatedPage></ProtectedRoute>} />
        <Route path="/conductor/:id" element={<ProtectedRoute><AnimatedPage><DriverPublicProfile /></AnimatedPage></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><UserManagement /></ProtectedRoute>} />
        <Route path="/admin/roles" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><RolesManager /></ProtectedRoute>} />
        <Route path="/admin/pagos" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><PaymentsPage /></ProtectedRoute>} />
        <Route path="/admin/retiros" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminPayouts /></ProtectedRoute>} />
        <Route path="/admin/config/pagos" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminPaymentConfig /></ProtectedRoute>} />
        <Route path="/admin/pagos/registrar" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><RegisterPaymentPage /></ProtectedRoute>} />

        <Route path="/admin" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminPanel /></ProtectedRoute>} />
        <Route path="/admin/conductores" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminDrivers /></ProtectedRoute>} />
        <Route path="/admin/servicios" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminServices /></ProtectedRoute>} />
        <Route path="/admin/tarifas" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminTariffs /></ProtectedRoute>} />
        <Route path="/admin/documentos" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminDocuments /></ProtectedRoute>} />
        <Route path="/admin/alertas-spoof" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminSpoofAlerts /></ProtectedRoute>} />
        <Route path="/admin/vehiculos" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminBikes /></ProtectedRoute>} />
        <Route path="/admin/turnos" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminShifts /></ProtectedRoute>} />
        <Route path="/admin/ganancias" element={<ProtectedRoute minLevel={4} redirectTo="/admin/login"><AdminEarnings /></ProtectedRoute>} />

        <Route path="/admin/catalogo-servicios" element={
          <ProtectedRoute minLevel={4} redirectTo="/admin/login">
            <AdminServiceCatalog />
          </ProtectedRoute>
        } />
        <Route path="/admin/conductores/perfil/:id" element={
          <ProtectedRoute minLevel={4} redirectTo="/admin/login">
            <AdminDriverProfile />
          </ProtectedRoute>
        } />
        <Route path="/admin/reviews" element={
          <ProtectedRoute minLevel={4} redirectTo="/admin/login">
            <AnimatedPage><AdminReviews /></AnimatedPage>
          </ProtectedRoute>
        } />
        <Route path="/admin/viajes-programados" element={
          <ProtectedRoute minLevel={4} redirectTo="/admin/login">
            <AdminScheduledServices />
          </ProtectedRoute>
        } />
        <Route path="/perfil" element={<ProtectedRoute><AnimatedPage><ProfilePage /></AnimatedPage></ProtectedRoute>} />
        <Route path="/notificaciones" element={<ProtectedRoute><AnimatedPage><NotificationsPage /></AnimatedPage></ProtectedRoute>} />
        {/* ── Soporte: accesible desde level 3 (support, admin, super_admin) ─ */}
        <Route path="/soporte" element={<ProtectedRoute minLevel={3}><AnimatedPage><SupportTicketsPage /></AnimatedPage></ProtectedRoute>} />
      </Routes>
    </AnimatePresence>
    </Suspense>
    </ErrorBoundary>
  );
}

const AnimatedPage = ({ children }) => {
  return (
    <motion.div
      className="app-page-wrapper"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
  );
};

export default App;
